import React, { useState, useEffect } from 'react';
import { FaHeart, FaFire, FaSortAmountDown, FaGlobe, FaThLarge, FaAlignLeft, FaLock, FaTh, FaSearch } from 'react-icons/fa';
import { motion } from 'framer-motion';

const ImageGridControls = ({
  // Filter props
  showFavoritesOnly = false,
  setShowFavoritesOnly = () => {},
  showPublicOnly = false,
  setShowPublicOnly = () => {},
  isHighlightingLikes = false,
  setIsHighlightingLikes = () => {},
  isHighlightingPublic = false,
  setIsHighlightingPublic = () => {},
  // Sort props
  sortOrder = 'newest',
  setSortOrder = () => {},
  // Group props
  isGroupedByPrompt = false,
  setIsGroupedByPrompt = () => {},
  // View props
  onFullscreenClick = () => {},
  isCompactGrid = false,
  setIsCompactGrid = () => {},
  // Auth & counts
  isAuthenticated = false,
  favoritesCount = 0,
  publicCount = 0,
  // Optional features
  showPublicToggle = false,
  showGrouping = false,
  showFullscreen = false,
  showCompactToggle = true,
  showPopularSort = true,
  showLikesControls = true,
  // Search props
  showSearch = false,
  setShowSearch = () => {},
}) => {
  // Track hover state for Most Popular option
  const [isPopularHovered, setIsPopularHovered] = useState(false);

  // Effect to sync hover state with highlighting
  useEffect(() => {
    if (sortOrder === 'popular') {
      setIsHighlightingLikes?.(isPopularHovered);
    }
  }, [isPopularHovered, sortOrder, setIsHighlightingLikes]);

  return (
    <div className="flex justify-between items-center">
      <div className="flex gap-4 sm:gap-4">
        {/* Favorites Filter */}
        {isAuthenticated && showLikesControls && (
          <button
            onClick={() => {
              setShowFavoritesOnly(!showFavoritesOnly);
              setIsHighlightingLikes(!showFavoritesOnly);
            }}
            onMouseEnter={() => setIsHighlightingLikes(true)}
            onMouseLeave={() => !showFavoritesOnly && setIsHighlightingLikes(false)}
            className={`group flex items-center gap-2 text-[18px] font-['VT323'] transition-all ${
              showFavoritesOnly ? 'bg-black/5 pl-2 pr-3 sm:pr-4 py-2 sm:py-1 rounded-full text-red-500' : 'text-black/20 hover:text-red-500'
            }`}
          >
            <div className="flex items-center gap-2">
              {showFavoritesOnly && (
                <div className="w-5 h-5 inline-flex justify-center items-center hover:bg-black/5 rounded-full">
                  <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                  </svg>
                </div>
              )}
              <FaHeart className="w-[18px] h-[18px] sm:w-[15px] sm:h-[15px]" />
              <span className="hidden sm:inline">Likes</span>
            </div>
          </button>
        )}

        {/* Public Filter */}
        {showPublicToggle && (
          <button
            onClick={() => {
              setShowPublicOnly(!showPublicOnly);
              setIsHighlightingPublic(!showPublicOnly);
            }}
            onMouseEnter={() => setIsHighlightingPublic(true)}
            onMouseLeave={() => !showPublicOnly && setIsHighlightingPublic(false)}
            className={`group flex items-center gap-2 text-[18px] font-['VT323'] transition-all ${
              showPublicOnly ? 'bg-black/5 pl-2 pr-3 sm:pr-4 py-2 sm:py-1 rounded-full text-green-500' : 'text-black/20 hover:text-green-500'
            }`}
          >
            <div className="flex items-center gap-2">
              {showPublicOnly && (
                <div className="w-5 h-5 inline-flex justify-center items-center hover:bg-black/5 rounded-full">
                  <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                  </svg>
                </div>
              )}
              <FaGlobe className="w-[18px] h-[18px] sm:w-[15px] sm:h-[15px]" />
              <span className="hidden sm:inline">Public</span>
            </div>
          </button>
        )}

        {/* Search Toggle */}
        <button
          onClick={() => setShowSearch(!showSearch)}
          className={`group flex items-center gap-2 text-[18px] font-['VT323'] transition-all ${
            showSearch ? 'text-blue-500' : 'text-black/20 hover:text-blue-500'
          }`}
        >
          <FaSearch className="w-[18px] h-[18px] sm:w-[15px] sm:h-[15px]" />
        </button>
      </div>

      {/* Right Controls */}
      <div className="flex items-center gap-4 sm:gap-4">
        {/* Compact Grid Toggle */}
        {showCompactToggle && (
          <motion.button
            onClick={() => setIsCompactGrid(!isCompactGrid)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`group flex items-center gap-2 p-2 sm:p-0 text-[18px] font-['VT323'] transition-all ${
              isCompactGrid ? 'text-blue-500' : 'text-black/20 hover:text-black/40'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline">Compact</span>
              <motion.div
                animate={{ scale: isCompactGrid ? 0.8 : 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 10 }}
              >
                <FaTh className="w-[18px] h-[18px] sm:w-[15px] sm:h-[15px]" />
              </motion.div>
            </div>
          </motion.button>
        )}

        {/* Fullscreen Toggle - Hidden on mobile */}
        {showFullscreen && (
          <button
            onClick={onFullscreenClick}
            className="group hidden md:flex items-center gap-2 p-2 sm:p-0 text-[18px] font-['VT323'] text-black/20 hover:text-black/40 transition-all"
          >
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline">Fullscreen</span>
              <FaThLarge className="w-[18px] h-[18px] sm:w-[15px] sm:h-[15px]" />
            </div>
          </button>
        )}

        {/* Group by Prompt Toggle */}
        {showGrouping && (
          <button
            onClick={() => setIsGroupedByPrompt(!isGroupedByPrompt)}
            className={`group flex items-center gap-2 text-[18px] font-['VT323'] text-black/20 transition-all ${
              isGroupedByPrompt ? 'bg-black/5 pl-2 pr-3 sm:pr-4 py-2 sm:py-1 rounded-full' : 'hover:text-black/40'
            }`}
          >
            <div className="flex items-center gap-2">
              {isGroupedByPrompt && (
                <div className="w-5 h-5 inline-flex justify-center items-center hover:bg-black/5 rounded-full">
                  <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                  </svg>
                </div>
              )}
              <span className="hidden sm:inline">Group</span>
              <FaAlignLeft className="w-[18px] h-[18px] sm:w-[15px] sm:h-[15px]" />
            </div>
          </button>
        )}

        {/* Sort Controls */}
        <button
          onClick={() => {
            setSortOrder(current => {
              if (current === 'newest') return 'oldest';
              if (current === 'oldest') return showPopularSort ? 'popular' : 'newest';
              return 'newest';
            });
          }}
          onMouseEnter={() => sortOrder === 'popular' && setIsPopularHovered(true)}
          onMouseLeave={() => setIsPopularHovered(false)}
          className="group flex items-center gap-2 p-2 sm:p-0 text-[18px] font-['VT323'] text-black/20 hover:text-black/40 transition-all"
        >
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline">
              {sortOrder === 'newest' && 'Newest First'}
              {sortOrder === 'oldest' && 'Oldest First'}
              {sortOrder === 'popular' && 'Most Popular'}
            </span>
            <motion.div
              animate={{ rotate: sortOrder === 'popular' ? 0 : sortOrder === 'oldest' ? 180 : 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 10 }}
            >
              {sortOrder === 'popular' ? (
                <FaFire className="w-[18px] h-[18px] sm:w-3.5 sm:h-3.5" />
              ) : (
                <FaSortAmountDown className="w-[18px] h-[18px] sm:w-3.5 sm:h-3.5" />
              )}
            </motion.div>
          </div>
        </button>
      </div>
    </div>
  );
};

export default ImageGridControls; 